import React from 'react';

import { Box } from '@opendoor/bricks/core';
import { MarginProps } from '@opendoor/bricks/system/theme';

import {
  CloseDateForm,
  CannotChangeCloseDate,
} from 'sellDirect/components/closingDash/changeCloseDate/CloseDateForm';
import {
  WizardSummary,
  getSignersRows,
  WizardHelloSignButtonsProps,
} from 'sellDirect/components/closingDash/WizardSummary';

import { ClosingTasks } from 'sellDirect/helpers/closingTasks';
import {
  FULL_DATE_AND_DAY_OF_WEEK_FORMAT,
  localDateFromStringOrNull,
} from 'shared/helpers/datetime';
import { ClosedOffer, UnderContractOffer } from 'sellDirect/OfferContext';

export interface ChangeCloseDateProps {
  /** Finalized offer */
  offer: UnderContractOffer | ClosedOffer;
  /** Current closing tasks */
  closingTasks: ClosingTasks;
  /** Current closing tasks */
  notRequestedCloseDate: boolean;
  /** Function to advance to the next step in the wizard */
  goToSummaryForm: () => void;
}

/**
 * Form for setting close date.
 * component-test-check-ignore storybook-check-ignore
 */
export const ChangeCloseDate: React.FC<ChangeCloseDateProps & MarginProps> = ({
  offer,
  closingTasks,
  notRequestedCloseDate,
  goToSummaryForm,
  ...marginProps
}) => {
  return (
    <Box {...marginProps}>
      {offer.canChangeCloseEscrowDate && offer.state === 'UnderContract' ? (
        <CloseDateForm
          offer={offer}
          availableCloseDates={
            closingTasks.setCloseDate ? closingTasks.setCloseDate.taskable.availableDates : []
          }
          goBackToSummary={() => goToSummaryForm()}
          canCancel={!notRequestedCloseDate}
        />
      ) : (
        <CannotChangeCloseDate
          shouldSeeAgentExperience={offer.shouldSeeAgentExperience}
          closeEscrowDate={offer.closeEscrowDate}
        />
      )}
    </Box>
  );
};

export interface ChangeCloseDateSummaryProps extends Omit<ChangeCloseDateProps, 'goToSummaryForm'> {
  /** Callback when user clicks Sign for a document and signatory pair */
  onClickSignButton: WizardHelloSignButtonsProps['onClickSignButton'];
  /** Function to advance to the previous step in the wizard */
  goToEditForm: () => void;
}

/**
 * Form to view setting close date summary.
 *
 * @throws Error if there is no close date (There should always be a close date)
 *
 * component-test-check-ignore storybook-check-ignore
 */
export const ChangeCloseDateSummary: React.FC<ChangeCloseDateSummaryProps & MarginProps> = ({
  offer,
  closingTasks,
  notRequestedCloseDate,
  onClickSignButton,
  goToEditForm,
  ...marginProps
}) => {
  const currentCloseDate = closingTasks.setCloseDate?.taskable.selectedDate;
  const newCloseDate = localDateFromStringOrNull(
    closingTasks.signChangeCloseOfEscrowAddendum?.taskable.addendum?.requestedCloseEscrowDate,
  );
  const closeDate = notRequestedCloseDate ? currentCloseDate : newCloseDate;
  if (!closeDate) {
    /**
     * If the user has not requested a new COE date, there must always be one in setCloseDate.taskable.selectedDate
     * Alternatively, if the user has requested a new close date, the signChangeCloseOfEscrowAddendum must have it
     *
     * If none of this is true (data is wrong, possibly corrupted) we throw an error
     */
    throw new Error('RequestChangeCloseDate expects there to be a close date.');
  }
  return (
    <Box {...marginProps}>
      <WizardSummary
        offer={offer}
        rows={[
          ...getSignersRows(closingTasks),
          {
            title: 'Close date',
            value: closeDate.format(FULL_DATE_AND_DAY_OF_WEEK_FORMAT),
            trackingId: 'close-date',
          },
        ]}
        trackingName="change-close-date-summary"
        onEdit={offer.canChangeCloseEscrowDate ? () => goToEditForm() : undefined}
        disableEdit={offer.state === 'Closed'}
        helloSignTask={closingTasks.signChangeCloseOfEscrowAddendum?.taskable}
        onClickSignButton={onClickSignButton}
      />
    </Box>
  );
};
